import { useState, useEffect, useCallback } from "react";
import { FaCheck, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import TableSkeleton from "../../../TableSkeleton";
import ConfirmActionModal from "../../../ConfirmDeleteModal";
import PathologyAgentModal from "./PathologyAgentModal";
import AccountExportOptions from "../../../AccountExportOptions";
import { saveToCache, readFromCache } from "../../../../services/pathologyCache";
import { getPendingAgents } from "../../../../services/pathologyOutbox";
import { onSyncStatusChange } from "../../../../services/pathologySyncEngine";
import Pagination from "../../../Shared/Pagination";

const PathologyAgentList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentToEdit, setAgentToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const itemsPerPage = 20;
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const API_BASE_URL = "https://api.muktihospital.com/api/pathology-agents";

  // Fetch agents from API
  const fetchAgents = useCallback(async () => {
    // Helper: map a pending outbox agent item to a row the UI can render.
    const mapPendingAgentToRow = (item) => {
      const p = item.payload || {};
      return {
        id: `offline-${item.clientId}`,
        agentCode: "PENDING",
        name: p.agentName || "",
        designation: p.designation || "",
        contactNo: p.contactNumber || "N/A",
        address: p.address || "",
        division: p.division || "Dhaka",
        district: p.district || "Dhaka",
        upazila: p.upazila || "",
        union: p.union || "",
        __offline: true,
      };
    };

    // Pending (offline-created) agents not yet synced — always shown on top.
    let pendingRows = [];
    try {
      const pending = await getPendingAgents();
      pendingRows = pending.map(mapPendingAgentToRow);
    } catch (e) {
      pendingRows = [];
    }

    try {
      const response = await axios.get(API_BASE_URL, {
        headers: { "x-api-key": API_KEY },
      });
      
      const data = Array.isArray(response.data.agents) ? response.data.agents : [];
      
      const sanitizedAgents = data.map((agent) => ({
        id: agent.id,
        agentCode: agent.agentCode || "",
        name: agent.agentName || "",
        designation: agent.designation || "",
        contactNo: agent.contactNumber || "N/A",
        address: agent.address || "",
        division: agent.division || "Dhaka",
        district: agent.district || "Dhaka",
        upazila: agent.upazila || "",
        union: agent.union || "",
      }));

      const sortedAgents = sanitizedAgents.sort((a, b) => {
        const numA = parseInt(a.agentCode.replace("PAG", ""), 10) || 0;
        const numB = parseInt(b.agentCode.replace("PAG", ""), 10) || 0;
        return numB - numA;
      });
      
      setAgents([...pendingRows, ...sortedAgents]);
      // Cache the SERVER list only (not the pending rows).
      saveToCache("pathology_agents", sortedAgents);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching pathology agents:", error);
      // Offline / server down: fall back to cached list + pending rows.
      if (error.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = (await readFromCache("pathology_agents")) || [];
        const merged = [...pendingRows, ...cached];
        setAgents(merged);
        if (merged.length > 0) {
          toast.info("Offline: showing cached agents.");
        }
      } else {
        // Still show pending rows even if server failed for non-network reasons.
        setAgents(pendingRows);
        toast.error("Failed to load pathology agents.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [refreshTrigger, fetchAgents]);

  // Auto-refresh the agent list when the sync engine finishes pushing
  // pending offline items. Mirrors the bill / test list behaviour.
  useEffect(() => {
    const unsub = onSyncStatusChange((status) => {
      if (status && status.justSynced && status.justSynced > 0) {
        // Small delay so the server commit settles before we re-fetch.
        setTimeout(() => {
          fetchAgents();
        }, 1200);
      }
    });
    return () => unsub();
  }, [fetchAgents]);

  // Search Logic
  const filteredAgents = agents.filter(
    (agent) =>
      (agent.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (agent.agentCode || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (agent.contactNo || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);

  const currentData = filteredAgents
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((agent, index) => ({
      ...agent,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((agent) => agent.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (agentId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(agentId)
        ? prevSelectedRows.filter((id) => id !== agentId)
        : [...prevSelectedRows, agentId]
    );
  };

  const handleDelete = async (agentIds) => {
    try {
      const deletePromises = agentIds.map(async (id) => {
        await axios.delete(`${API_BASE_URL}/delete/${id}`, {
          headers: { "x-api-key": API_KEY },
        });
      });

      await Promise.all(deletePromises);
      toast.success(`${agentIds.length} agent(s) deleted successfully!`);
      setSelectedRows([]);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      toast.error("Failed to delete agent(s).");
    }
  };

  const handleEdit = (agent) => {
    setAgentToEdit(agent);
    setAgentModalOpen(true);
  };

  const handleSaveAgent = (savedAgent, isEditMode) => {
    setRefreshTrigger((prev) => prev + 1);
    setAgentModalOpen(false);
    setAgentToEdit(null);
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);
  const agentSummaryText = `Total Pathology Agents Listed: ${filteredAgents.length}`;

  const headers = ["Serial No", "Agent Code", "Agent Name", "Designation", "Contact Number"];
  const dataMapper = (item) =>
    [item.displayId, item.agentCode, item.name, item.designation || "N/A", item.contactNo || "N/A"].join("\t");

  const skeletonColumns = [
    { width: "40px", height: "16px", className: "text-left", skeletonClassName: "my-5 mx-3" },
    { width: "80px", height: "16px", className: "text-left py-5 px-3", skeletonClassName: "" },
    { width: "100px", height: "16px", className: "text-left py-5 px-3", skeletonClassName: "" },
    { width: "100px", height: "16px", className: "text-left py-5 px-3", skeletonClassName: "" },
    { width: "120px", height: "16px", className: "text-left py-5 px-3", skeletonClassName: "" },
    { width: "80px", height: "16px", className: "text-left p-3", skeletonClassName: "ml-auto" },
    { width: "80px", height: "16px", className: "text-right p-3", skeletonClassName: "ml-auto" },
  ];

  return (
    <div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header Section */}
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Pathology Agent List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by name, code, or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={() => {
                  setAgentToEdit(null);
                  setAgentModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Agent
              </button>
              <AccountExportOptions
                  data={filteredAgents}
                  title="Pathology Official Agent Database"
                  subTitle={agentSummaryText}
                  headers={headers}
                  dataMapper={(item, index) => {
                    const mappedItem = {
                      ...item,
                      displayId: index + 1
                    };
                    return dataMapper(mappedItem);
                  }}
                  dropdownOpen={dropdownOpen}
                  toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
                />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto print-wrapper">
          <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left print-hidden-in-print">
                      <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agent-select-all"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              currentData.length > 0 &&
                              currentData.every((item) =>
                                selectedRows.includes(item.id)
                              )
                            }
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="agent-select-all"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                  </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Serial No</th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Agent Code</th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Agent Name</th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Designation</th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Contact Number</th>
                <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} baseColor="#e5e7eb" highlightColor="#f3f4f6" />
              ) : filteredAgents.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-5 text-center text-gray-600">No agents found.</td>
                </tr>
              ) : (
                currentData.map((agent, index) => (
                  <tr key={agent.id} className="border-b hover:bg-gray-50">
                    <td className="text-left print-hidden-in-print">
                      <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id={`agent-row-${agent.id}`}
                            className="hidden peer print-hidden"
                            onChange={() => handleRowSelect(agent.id)}
                            checked={selectedRows.includes(agent.id)}
                            disabled={loading || agent.__offline}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`agent-row-${agent.id}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                    </td>
                    <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{agent.displayId}</td>
                    <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{agent.agentCode}</td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {agent.name}
                      {agent.__offline && (
                        <span className="ml-2 inline-block bg-orange-50 text-orange-600 border border-orange-200 text-[10px] font-medium px-2 py-0.5 rounded-full align-middle">
                          Pending Sync
                        </span>
                      )}
                    </td>
                    <td className="text-left uppercase font-inter font-normal text-sm text-gray-500 py-5 px-3">
                      {agent.designation || <span className="text-gray-400 italic text-xs">Normal Agent</span>}
                    </td>
                    <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{agent.contactNo || "N/A"}</td>
                    <td className="p-3 text-right space-x-2 print-hidden-in-print">
                      {agent.__offline ? (
                        <span className="text-xs text-gray-400 italic">
                          Waiting for sync
                        </span>
                      ) : (
                        <>
                      <button
                        onClick={() => handleEdit(agent)}
                        className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRows([agent.id]);
                          setDeleteModalOpen(true);
                        }}
                        className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                        title="Delete"
                      >
                        <RiDeleteBin6Line />
                      </button>
                      </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Reusable Pagination */}
        <div className="print-hidden">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={filteredAgents.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Modals */}
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="pathology agent"
        title="Confirm Delete"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} agent(s)?`
            : `Do you really want to delete this agent?`
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <PathologyAgentModal
        isOpen={agentModalOpen}
        onClose={() => {
          setAgentModalOpen(false);
          setAgentToEdit(null);
        }}
        onSave={handleSaveAgent}
        agentToEdit={agentToEdit}
      />
    </div>
  );
};

export default PathologyAgentList;